/* Research / Ürün Detay — Product > Variants > Suppliers.
 * Owner: /research/p/:pid
 * Sourcer: /r/:slug/p/:pid
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Plus, Trash2, Edit2, X, Camera, Lock, Package,
    DollarSign, Euro, MapPin, Clock, Loader2, ChevronLeft, Save, Layers,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtEur = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

function getHeaders() {
    const slug = sessionStorage.getItem('research_share_slug');
    const token = sessionStorage.getItem('research_share_token');
    if (slug && token) {
        return { 'X-Share-Slug': slug, 'X-Share-Token': token };
    }
    return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function ResearchProduct({ shareMode = false }) {
    const navigate = useNavigate();
    const { pid, slug } = useParams();
    const [pinVerified, setPinVerified] = useState(!shareMode);
    const [product, setProduct] = useState(null);
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingImg, setUploadingImg] = useState(false);
    const [editingProduct, setEditingProduct] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // 'product' or `v:${vid}`
    const [newVariantName, setNewVariantName] = useState('');
    const [addingVariant, setAddingVariant] = useState(false);

    useEffect(() => {
        if (!shareMode) return;
        const t = sessionStorage.getItem('research_share_token');
        const s = sessionStorage.getItem('research_share_slug');
        if (t && s === slug) setPinVerified(true);
    }, [shareMode, slug]);

    const load = useCallback(async () => {
        if (!pinVerified) return;
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/research/products/${pid}`, { headers: getHeaders() });
            setProduct(r.data.product);
            setVariants(r.data.variants || []);
        } catch (e) {
            if (e.response?.status === 401 && shareMode) {
                setPinVerified(false);
                sessionStorage.removeItem('research_share_token');
            } else if (e.response?.status === 404) {
                toast.error('Ürün bulunamadı');
                navigate(shareMode ? `/r/${slug}` : '/research');
            } else {
                toast.error(e.response?.data?.detail || 'Yüklenemedi');
            }
        } finally { setLoading(false); }
    }, [pid, pinVerified, shareMode, slug, navigate]);

    useEffect(() => { load(); }, [load]);

    const goBack = () => navigate(shareMode ? `/r/${slug}` : '/research');

    const uploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingImg(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await axios.post(`${API}/api/research/products/${pid}/image`, fd, {
                headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
            });
            setProduct({ ...product, image_url: r.data.image_url });
            toast.success('Fotoğraf yüklendi');
        } catch (err) { toast.error(err.response?.data?.detail || 'Foto hatası'); }
        finally { setUploadingImg(false); }
    };

    const deleteProduct = async () => {
        if (deleteConfirm !== 'product') {
            setDeleteConfirm('product');
            setTimeout(() => setDeleteConfirm((c) => (c === 'product' ? null : c)), 4000);
            return;
        }
        try {
            await axios.delete(`${API}/api/research/products/${pid}`, { headers: getHeaders() });
            toast.success('Ürün silindi');
            goBack();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const addVariant = async () => {
        const name = newVariantName.trim();
        if (!name) { toast.error('Varyant adı gerekli'); return; }
        setAddingVariant(true);
        try {
            await axios.post(`${API}/api/research/products/${pid}/variants`,
                { name }, { headers: getHeaders() });
            setNewVariantName('');
            toast.success('Varyant eklendi');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setAddingVariant(false); }
    };

    if (shareMode && !pinVerified) {
        return <PinGate slug={slug} onSuccess={() => setPinVerified(true)} />;
    }

    if (loading || !product) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 size={28} className="animate-spin text-indigo-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Sticky header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button onClick={goBack} className="p-1 -ml-1" data-testid="research-product-back">
                        <ChevronLeft size={20} />
                    </button>
                    <h1 className="flex-1 text-base font-bold text-gray-900 truncate">{product.name}</h1>
                    <button onClick={() => setEditingProduct(true)}
                        className="h-8 px-2 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded inline-flex items-center gap-1"
                        data-testid="edit-product-btn">
                        <Edit2 size={11} /> Düzenle
                    </button>
                    <button onClick={deleteProduct}
                        className={`h-8 px-2 text-xs font-bold rounded inline-flex items-center gap-1 ${deleteConfirm === 'product' ? 'bg-rose-600 text-white' : 'text-rose-700 bg-rose-50 hover:bg-rose-100'}`}
                        data-testid="del-product">
                        <Trash2 size={11} /> {deleteConfirm === 'product' ? 'Emin misin?' : 'Sil'}
                    </button>
                </div>
            </div>

            <div className="max-w-3xl mx-auto pb-12">
                {/* Photo — kompakt */}
                <div className="relative bg-gray-100 h-40 sm:h-48">
                    {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
                    ) : (
                        <div className="flex items-center justify-center h-full text-gray-300"><Package size={36} /></div>
                    )}
                    <label className="absolute bottom-2 right-2 bg-white shadow-md px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 cursor-pointer hover:bg-gray-50">
                        {uploadingImg ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                        {product.image_url ? 'Değiştir' : 'Fotoğraf Ekle'}
                        <input type="file" accept="image/*" capture="environment" onChange={uploadImage} className="hidden" data-testid="upload-img" />
                    </label>
                </div>

                {/* Add Variant */}
                <div className="px-4 py-3 bg-white border-b">
                    <div className="flex items-center gap-2">
                        <Layers size={16} className="text-indigo-600" />
                        <input
                            value={newVariantName}
                            onChange={(e) => setNewVariantName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addVariant()}
                            placeholder="Yeni varyant (örn: 8oz, Kırmızı XL)"
                            className="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-md focus:border-indigo-500 outline-none"
                            data-testid="new-variant-input"
                        />
                        <button onClick={addVariant} disabled={addingVariant || !newVariantName.trim()}
                            className="h-10 px-3 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-md inline-flex items-center gap-1"
                            data-testid="add-variant-btn">
                            {addingVariant ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            Ekle
                        </button>
                    </div>
                </div>

                {/* Variants list */}
                <div className="px-4 py-3 space-y-3">
                    {variants.length === 0 ? (
                        <div className="text-center py-8 text-sm text-gray-400">
                            Henüz varyant yok. Yukarıdan ilk varyantı ekleyin.
                        </div>
                    ) : (
                        variants.map((v) => (
                            <VariantBlock
                                key={v.id}
                                variant={v}
                                deleteConfirm={deleteConfirm}
                                setDeleteConfirm={setDeleteConfirm}
                                onReload={load}
                            />
                        ))
                    )}
                </div>

                {/* Notes */}
                {product.notes && (
                    <div className="px-4 py-3 border-t bg-white">
                        <h3 className="text-xs font-bold text-gray-500 uppercase mb-1">Notlar</h3>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{product.notes}</p>
                    </div>
                )}
            </div>

            {editingProduct && <EditProductModal product={product} onClose={() => setEditingProduct(false)} onSaved={(p) => { setProduct(p); setEditingProduct(false); }} />}
        </div>
    );
}

// ─── Variant Block ──────────────────────────────────────────────────────────
function VariantBlock({ variant, deleteConfirm, setDeleteConfirm, onReload }) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(variant.name);
    const [showAddSup, setShowAddSup] = useState(false);
    const [editingSup, setEditingSup] = useState(null);

    const saveName = async () => {
        if (!name.trim()) { toast.error('Ad gerekli'); return; }
        try {
            await axios.patch(`${API}/api/research/variants/${variant.id}`,
                { name: name.trim() }, { headers: getHeaders() });
            toast.success('Güncellendi');
            setEditing(false);
            onReload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const deleteVariant = async () => {
        const key = `v:${variant.id}`;
        if (deleteConfirm !== key) {
            setDeleteConfirm(key);
            setTimeout(() => setDeleteConfirm((c) => (c === key ? null : c)), 4000);
            return;
        }
        try {
            await axios.delete(`${API}/api/research/variants/${variant.id}`, { headers: getHeaders() });
            toast.success('Varyant silindi');
            onReload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const deleteSupplier = async (sid) => {
        try {
            await axios.delete(`${API}/api/research/suppliers/${sid}`, { headers: getHeaders() });
            toast.success('Tedarikçi silindi');
            onReload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const suppliers = variant.suppliers || [];
    const minPrice = suppliers.length ? Math.min(...suppliers.map((s) => s.price_eur_normalized || 0).filter((p) => p > 0)) : 0;
    const isConfirming = deleteConfirm === `v:${variant.id}`;

    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`variant-${variant.id}`}>
            {/* Header */}
            <div className="px-3 py-2 bg-indigo-50/50 border-b flex items-center gap-2">
                <Layers size={14} className="text-indigo-600 shrink-0" />
                {editing ? (
                    <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && saveName()}
                        className="flex-1 h-8 px-2 text-sm border border-indigo-300 rounded"
                        data-testid={`variant-name-edit-${variant.id}`} />
                ) : (
                    <h3 className="flex-1 text-sm font-bold text-gray-900 truncate">{variant.name}</h3>
                )}
                {minPrice > 0 && !editing && (
                    <span className="text-xs font-bold text-emerald-700">{fmtEur(minPrice)}</span>
                )}
                {editing ? (
                    <>
                        <button onClick={saveName} className="h-7 px-2 text-[11px] font-bold text-white bg-indigo-600 rounded inline-flex items-center gap-1" data-testid={`variant-save-${variant.id}`}>
                            <Save size={11} /> Kaydet
                        </button>
                        <button onClick={() => { setEditing(false); setName(variant.name); }} className="h-7 px-2 text-[11px] font-bold text-gray-700 bg-gray-200 rounded">
                            İptal
                        </button>
                    </>
                ) : (
                    <>
                        <button onClick={() => setEditing(true)} className="h-7 px-1.5 text-[11px] font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded inline-flex items-center gap-1" data-testid={`variant-edit-${variant.id}`}>
                            <Edit2 size={10} />
                        </button>
                        <button onClick={deleteVariant}
                            className={`h-7 px-2 text-[11px] font-bold rounded inline-flex items-center gap-1 ${isConfirming ? 'bg-rose-600 text-white' : 'text-rose-700 bg-rose-50 hover:bg-rose-100'}`}
                            data-testid={`variant-del-${variant.id}`}>
                            <Trash2 size={10} /> {isConfirming ? 'Emin misin?' : 'Sil'}
                        </button>
                    </>
                )}
            </div>

            {/* Suppliers */}
            <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-bold uppercase text-gray-500">Tedarikçiler ({suppliers.length})</div>
                    <button onClick={() => setShowAddSup(true)}
                        className="h-7 px-2 text-[11px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded inline-flex items-center gap-1"
                        data-testid={`add-supplier-${variant.id}`}>
                        <Plus size={11} /> Tedarikçi
                    </button>
                </div>
                {suppliers.length === 0 ? (
                    <div className="text-center py-3 text-xs text-gray-400">Tedarikçi yok</div>
                ) : (
                    <div className="space-y-2">
                        {suppliers.map((s) => (
                            <SupplierCard key={s.id} supplier={s}
                                onEdit={() => setEditingSup(s)}
                                onDelete={() => deleteSupplier(s.id)} />
                        ))}
                    </div>
                )}
            </div>

            {showAddSup && <SupplierModal variantId={variant.id} onClose={() => setShowAddSup(false)} onSaved={onReload} />}
            {editingSup && <SupplierModal variantId={variant.id} supplier={editingSup} onClose={() => setEditingSup(null)} onSaved={onReload} />}
        </div>
    );
}

// ─── PIN Gate ───────────────────────────────────────────────────────────────
function PinGate({ slug, onSuccess }) {
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e?.preventDefault();
        if (pin.length < 4) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API}/api/research/share/${slug}/verify`, { pin });
            sessionStorage.setItem('research_share_slug', slug);
            sessionStorage.setItem('research_share_token', r.data.token);
            onSuccess();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'PIN hatalı');
            setPin('');
        } finally { setBusy(false); }
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center p-4">
            <form onSubmit={submit} className="w-full max-w-xs bg-white rounded-2xl shadow-xl p-6 text-center" data-testid="pin-gate">
                <div className="w-12 h-12 mx-auto bg-indigo-100 rounded-full flex items-center justify-center">
                    <Lock size={20} className="text-indigo-600" />
                </div>
                <h2 className="mt-3 text-lg font-bold text-gray-900">Araştırma Linki</h2>
                <p className="text-xs text-gray-500 mt-1">Devam etmek için PIN&apos;i girin</p>
                <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    inputMode="numeric" autoFocus
                    className="mt-4 w-full h-14 text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg focus:border-indigo-500 outline-none"
                    placeholder="••••"
                    data-testid="pin-input" />
                <button type="submit" disabled={busy || pin.length < 4}
                    className="mt-3 w-full h-12 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-lg"
                    data-testid="pin-submit">
                    {busy ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Giriş'}
                </button>
            </form>
        </div>
    );
}

function SupplierCard({ supplier: s, onEdit, onDelete }) {
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5" data-testid={`sup-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-sm truncate">{s.supplier_name}</div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 flex-wrap">
                        {s.country && (<span className="inline-flex items-center gap-0.5"><MapPin size={9} />{s.country}</span>)}
                        {s.moq > 0 && (<span>MOQ: {s.moq}</span>)}
                        {s.lead_time_days > 0 && (<span className="inline-flex items-center gap-0.5"><Clock size={9} />{s.lead_time_days}g</span>)}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="font-bold text-gray-900 text-sm flex items-center gap-0.5 justify-end">
                        {s.currency === 'USD' && <DollarSign size={11} />}
                        {s.currency === 'EUR' && <Euro size={11} />}
                        {s.currency === 'CNY' && <span className="text-sm">¥</span>}
                        {s.price.toFixed(2)}
                    </div>
                    {(s.currency === 'USD' || s.currency === 'CNY') && (
                        <div className="text-[10px] text-gray-500">≈ {fmtEur(s.price_eur_normalized)}</div>
                    )}
                </div>
            </div>
            {s.contact && <div className="mt-1 text-[11px] text-gray-600 truncate">📞 {s.contact}</div>}
            {s.notes && <div className="mt-1 text-[11px] text-gray-500 line-clamp-2">{s.notes}</div>}
            <div className="mt-2 flex items-center gap-1 justify-end">
                <button onClick={onEdit} className="h-6 px-2 text-[10px] font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded inline-flex items-center gap-1" data-testid={`sup-edit-${s.id}`}>
                    <Edit2 size={10} /> Düzenle
                </button>
                <button onClick={onDelete} className="h-6 px-2 text-[10px] font-bold text-rose-700 bg-white border border-rose-200 hover:bg-rose-50 rounded inline-flex items-center gap-1" data-testid={`sup-del-${s.id}`}>
                    <Trash2 size={10} /> Sil
                </button>
            </div>
        </div>
    );
}

function SupplierModal({ variantId, supplier, onClose, onSaved }) {
    const isEdit = !!supplier;
    const [form, setForm] = useState(supplier || {
        supplier_name: '', country: '', price: '', currency: 'USD', moq: '', lead_time_days: '', contact: '', notes: '',
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm({ ...form, [k]: v });
    const save = async () => {
        if (!form.supplier_name?.trim() || !form.price) { toast.error('Tedarikçi adı ve fiyat gerekli'); return; }
        setSaving(true);
        try {
            const payload = {
                supplier_name: form.supplier_name, country: form.country, currency: form.currency,
                price: parseFloat(form.price), moq: parseInt(form.moq) || 0,
                lead_time_days: parseInt(form.lead_time_days) || 0, contact: form.contact, notes: form.notes,
            };
            if (isEdit) {
                await axios.patch(`${API}/api/research/suppliers/${supplier.id}`, payload, { headers: getHeaders() });
            } else {
                await axios.post(`${API}/api/research/variants/${variantId}/suppliers`, payload, { headers: getHeaders() });
            }
            toast.success(isEdit ? 'Güncellendi' : 'Eklendi');
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setSaving(false); }
    };
    return (
        <Modal onClose={onClose} title={isEdit ? 'Tedarikçi Düzenle' : 'Yeni Tedarikçi'}>
            <Field label="Firma Adı *">
                <input value={form.supplier_name} onChange={(e) => set('supplier_name', e.target.value)} autoFocus
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm" data-testid="sup-name" />
            </Field>
            <div className="grid grid-cols-3 gap-2">
                <Field label="Para">
                    <select value={form.currency} onChange={(e) => set('currency', e.target.value)}
                        className="w-full h-11 px-2 border border-gray-300 rounded-md text-sm" data-testid="sup-cur">
                        <option value="USD">USD</option><option value="EUR">EUR</option><option value="CNY">CNY (¥)</option>
                    </select>
                </Field>
                <Field label="Fiyat *">
                    <input type="number" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)}
                        className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm" data-testid="sup-price" />
                </Field>
                <Field label="Ülke">
                    <input value={form.country} onChange={(e) => set('country', e.target.value.toUpperCase().slice(0, 2))}
                        placeholder="CN" className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm uppercase" data-testid="sup-country" />
                </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="MOQ"><input type="number" value={form.moq} onChange={(e) => set('moq', e.target.value)}
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm" data-testid="sup-moq" /></Field>
                <Field label="Lead Time (gün)"><input type="number" value={form.lead_time_days} onChange={(e) => set('lead_time_days', e.target.value)}
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm" data-testid="sup-lead" /></Field>
            </div>
            <Field label="İletişim (email/wechat/tel)">
                <input value={form.contact} onChange={(e) => set('contact', e.target.value)}
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm" data-testid="sup-contact" />
            </Field>
            <Field label="Not">
                <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="sup-notes" />
            </Field>
            <button onClick={save} disabled={saving}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md disabled:opacity-50" data-testid="sup-save">
                {saving ? <Loader2 size={18} className="animate-spin mx-auto" /> : 'Kaydet'}
            </button>
        </Modal>
    );
}

function EditProductModal({ product, onClose, onSaved }) {
    const [name, setName] = useState(product.name || '');
    const [notes, setNotes] = useState(product.notes || '');
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (!name.trim()) { toast.error('Ürün adı gerekli'); return; }
        setSaving(true);
        try {
            const r = await axios.patch(`${API}/api/research/products/${product.id}`,
                { name, notes }, { headers: getHeaders() });
            toast.success('Güncellendi');
            onSaved(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
        finally { setSaving(false); }
    };
    return (
        <Modal onClose={onClose} title="Ürünü Düzenle">
            <Field label="Ürün Adı *">
                <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm" data-testid="edit-name" />
            </Field>
            <Field label="Notlar">
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="edit-notes" />
            </Field>
            <button onClick={save} disabled={saving}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md disabled:opacity-50 inline-flex items-center justify-center gap-1"
                data-testid="edit-save">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <><Save size={14} /> Kaydet</>}
            </button>
        </Modal>
    );
}

function Modal({ children, title, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
            <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                    <h2 className="font-bold text-gray-900">{title}</h2>
                    <button onClick={onClose} className="p-1"><X size={18} /></button>
                </div>
                <div className="p-4 space-y-3">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wide block mb-1">{label}</label>
            {children}
        </div>
    );
}
